import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommunicationService } from '../../services/communication.service';
import { IPaginatedResult } from '../../interfaces/mailing/IPaginatedResult';
import { ICommunication } from '../../interfaces/communication/ICommunication';
import { PageEvent } from '@angular/material/paginator';
import { asyncPoll } from '../../helpers/async-poll';
import { IQueryCommunication } from '../../interfaces/queries/IQueryCommunication';
import { disableBackgroundScrolling, enableBackgroundScrolling } from '../../helpers/helpers';
import { IReport } from '@shared/interfaces';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';

@Component({
    selector: 'app-communication',
    templateUrl: './communication.component.html',
    styleUrls: ['./communication.component.scss'],
    standalone: false
})
export class CommunicationComponent implements OnInit {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    placeholderArray: { id: number; report: IReport }[] = Array.from({ length: 10 }, (_, id) => ({
        id,
        report: {} as IReport
    }));

    @ViewChild('itemContainer')
    itemContainer: ElementRef;
    pagedData: IPaginatedResult<ICommunication>;
    isVisible = false;
    event: PageEvent;
    loading: boolean;
    type: 'all' | 'unread' | 'task' = 'all';
    previousType: 'all' | 'unread' | 'task' = 'all';
    unreadMessages = 0;

    constructor(private communicationService: CommunicationService) {}

    get paginatedData(): ICommunication[] {
        return this.pagedData?.items ?? [];
    }

    async ngOnInit() {
        this.communicationService.getUnreadMessagesCount().then(e => (this.unreadMessages = e));
        this.loadData();

        asyncPoll<void>(async (): Promise<void> => {
            const newItems = await this.communicationService.poll();

            if (newItems?.length) {
                const items =
                    this.type === 'all'
                        ? newItems
                        : this.type === 'unread'
                          ? newItems.filter(e => e.isNew)
                          : newItems.filter(e => e.communicationType === this.type);

                if (items.length) {
                    this.pagedData = {
                        ...this.pagedData,
                        items: [...items, ...(this.pagedData?.items ?? [])]
                    };
                }

                this.unreadMessages += newItems.length;

                if (this.isVisible && items.length) {
                    setTimeout(async () => await this.addSeen(), 350);
                }
            }

            return;
        }, 60 * 1000);
    }

    async loadData(event?: PageEvent) {
        if (!event && this.type !== this.previousType && this.type !== 'all') {
            this.pagedData = null;
        }

        if (
            !this.pagedData ||
            !event ||
            (this.pagedData &&
                event.pageIndex > this.pagedData.page &&
                (event.pageIndex + 1) * event.pageSize > this.pagedData.items.length)
        ) {
            const query: IQueryCommunication = {
                pagination: {
                    page: event?.pageIndex ?? 0,
                    pageSize: 20
                },
                type: this.type
            };

            this.previousType = this.type;

            const paginatedResult = await this.communicationService.getAll(query);

            if (event) {
                this.pagedData = {
                    ...paginatedResult,
                    items: [...paginatedResult.items, ...(this.pagedData?.items ?? [])]
                };
            } else {
                this.pagedData = paginatedResult;
                setTimeout(() => this.setScrollListener(), 50);
            }
        }

        this.event = event ?? ({ pageIndex: 0, pageSize: 20 } as PageEvent);
    }

    async toggleView() {
        this.isVisible = !this.isVisible;

        if (this.isVisible && this.paginatedData.some(e => e.isNew)) {
            await this.addSeen();
        } else if (this.isVisible && this.paginatedData) {
            this.loadData();
        }

        if (this.isVisible) {
            disableBackgroundScrolling();
        } else {
            enableBackgroundScrolling();
        }
    }

    private async addSeen() {
        await this.communicationService.addSeenBy();
        this.paginatedData.forEach(e => (e.isNew = false));
        this.unreadMessages = 0;
    }

    private setScrollListener() {
        const itemContainer = this.itemContainer?.nativeElement;
        if (itemContainer) {
            itemContainer.onscroll = async () => {
                if (this.pagedData.hasMore) {
                    const pos = (itemContainer.scrollTop || itemContainer.scrollTop) + itemContainer.offsetHeight;
                    const max = itemContainer.scrollHeight;

                    if (pos >= max - 1 && !this.loading) {
                        this.loading = true;
                        this.event.pageIndex++;
                        await this.loadData(this.event);
                        this.loading = false;
                    }
                }
            };
        }
    }
}
